import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

export default function ThankYou() {
  const { t, logo } = useLang();
  const { state } = useLocation();
  const guide = state?.guide || "/code-echo-response-guide.pdf";
  const name = state?.name;

  useEffect(() => {
    // Auto-trigger the download once the page loads
    const timer = setTimeout(() => {
      const a = document.createElement("a");
      a.href = guide;
      a.download = "Code-Echo-Response-Guide.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }, 700);
    return () => clearTimeout(timer);
  }, [guide]);

  return (
    <main
      data-testid="thank-you-page"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0B0C10] px-5 text-center text-white font-body"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#12131C] to-[#0B0C10]" />
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 h-[420px] w-[620px] rounded-full blur-[140px] opacity-20"
        style={{ background: "radial-gradient(circle, #0057B7, transparent 70%)" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col items-center max-w-xl"
      >
        <img src={logo} alt="Code Echo First Aid" className="h-12 w-auto mb-10" />
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <CheckCircle2 className="h-16 w-16 text-[#0057B7]" strokeWidth={1.4} />
        </motion.div>

        <h1 className="mt-8 font-heading text-4xl sm:text-5xl font-semibold tracking-tighter uppercase leading-none">
          {name ? t.ty_title_named(name) : t.ty_title}
        </h1>
        <p className="mt-6 text-sm sm:text-lg text-zinc-300">
          {t.ty_body}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
          <a
            href={guide}
            download="Code-Echo-Response-Guide.pdf"
            data-testid="thankyou-download"
            className="group relative overflow-hidden rounded-sm bg-[#0057B7] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:glow-blue"
          >
            <span className="relative z-10 flex items-center gap-3">
              <Download className="h-4 w-4" /> {t.ty_download}
            </span>
            <span className="absolute inset-0 -translate-y-full bg-[#003f86] transition-transform duration-300 group-hover:translate-y-0" />
          </a>
          <Link
            to="/"
            data-testid="thankyou-home"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> {t.ty_home}
          </Link>
        </div>
      </motion.div>

      <p className="absolute bottom-8 text-xs text-zinc-400">
        {t.copyright}
      </p>
    </main>
  );
}
